/**
 * Transform raw finance records from the API into display-ready transaction rows.
 * Each record may produce an income row and/or an expense row based on real DB values.
 */

export const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const deriveStatus = (record) => {
    const checkout = record.check_out_date ? new Date(record.check_out_date) : null;
    const now = new Date();
    if (!checkout || checkout >= now) return "Active";
    return "Paid";
};

const recordDate = (record) => record.check_in_date || record.created_at;

export const recordsToTransactions = (records) => {
    if (!Array.isArray(records)) return [];

    const transactions = [];

    records.forEach((record) => {
        const date = recordDate(record);
        const currency = record.currency || "€";
        const status = deriveStatus(record);
        const income = parseFloat(record.amount_paid ?? 0);
        const expense = parseFloat(record.agent_expenses ?? 0);
        const tenant = (record.tenant_name || "").trim();

        if (income > 0) {
            transactions.push({
                id: `${record.id}-income`,
                recordId: record.id,
                date,
                dateFormatted: formatDate(date),
                description: tenant ? `Rental — ${tenant}` : "Rental income",
                category: "Rental Income",
                type: "Income",
                amount: income,
                currency,
                status,
                year: date ? new Date(date).getFullYear() : null,
                month: date ? new Date(date).getMonth() + 1 : null,
            });
        }

        if (expense > 0) {
            const desc = (record.expenses_description || "").trim();
            transactions.push({
                id: `${record.id}-expense`,
                recordId: record.id,
                date,
                dateFormatted: formatDate(date),
                description: desc || "Agent expenses",
                category: "Agent Expenses",
                type: "Expense",
                amount: expense,
                currency,
                status: "Paid",
                year: date ? new Date(date).getFullYear() : null,
                month: date ? new Date(date).getMonth() + 1 : null,
            });
        }
    });

    return transactions.sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        return db - da;
    });
};

export const computeFinancialSummary = (transactions) => {
    const list = transactions || [];
    const totalIncome = list
        .filter((tx) => tx.type === "Income")
        .reduce((sum, tx) => sum + parseFloat(tx.amount ?? 0), 0);
    const totalExpenses = list
        .filter((tx) => tx.type === "Expense")
        .reduce((sum, tx) => sum + parseFloat(tx.amount ?? 0), 0);

    let lastDate = null;
    list.forEach((tx) => {
        if (!tx.date) return;
        const t = new Date(tx.date).getTime();
        if (!lastDate || t > lastDate) lastDate = t;
    });

    const currency = list.find((tx) => tx.currency)?.currency || "€";

    return {
        totalIncome,
        totalExpenses,
        currentBalance: totalIncome - totalExpenses,
        transactionCount: list.length,
        lastTransaction: lastDate ? formatDate(new Date(lastDate)) : "—",
        currency,
    };
};

export const filterTransactions = (transactions, { year, month, type }) => {
    return transactions.filter((tx) => {
        if (year && tx.year !== parseInt(year, 10)) return false;
        if (month && tx.month !== parseInt(month, 10)) return false;
        if (type === "income" && tx.type !== "Income") return false;
        if (type === "expense" && tx.type !== "Expense") return false;
        return true;
    });
};

const recordDateParts = (record) => {
    const date = record.check_in_date || record.created_at;
    if (!date) return { year: null, month: null, timestamp: 0 };
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return { year: null, month: null, timestamp: 0 };
    return { year: d.getFullYear(), month: d.getMonth() + 1, timestamp: d.getTime() };
};

export const filterFinanceRecords = (records, { year, month } = {}) => {
    if (!Array.isArray(records)) return [];
    return records.filter((record) => {
        const { year: y, month: m } = recordDateParts(record);
        if (year && y !== parseInt(year, 10)) return false;
        if (month && m !== parseInt(month, 10)) return false;
        return true;
    });
};

export const computeRecordsSummary = (records) => {
    const list = records || [];
    const totalIncome = list.reduce((sum, r) => sum + parseFloat(r.amount_paid ?? 0), 0);
    const totalExpenses = list.reduce((sum, r) => sum + parseFloat(r.agent_expenses ?? 0), 0);

    let lastTimestamp = 0;
    list.forEach((r) => {
        const { timestamp } = recordDateParts(r);
        if (timestamp > lastTimestamp) lastTimestamp = timestamp;
    });

    const currency = list.find((r) => r.currency)?.currency || "€";

    return {
        totalIncome,
        totalExpenses,
        currentBalance: totalIncome - totalExpenses,
        transactionCount: list.length,
        lastTransaction: lastTimestamp ? formatDate(new Date(lastTimestamp)) : "—",
        currency,
    };
};

export const calculateRemaining = (paid, expenses) => {
    const a = parseFloat(paid ?? 0);
    const e = parseFloat(expenses ?? 0);
    return a - e;
};

export const formatCurrency = (amount, currency = "€") => {
    const n = parseFloat(amount ?? 0);
    const symbol = currency === "EUR" ? "€" : currency;
    return `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};
